/**
 * Home Page Component
 * Landing page with Hero, Value Props, How It Works, Who Is This For, Why CODEMAKER, and CTA
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import './Home.css';

// SVG Icons
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const BuildingIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
    </svg>
);

const UsersIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

// Value proposition data
const valueProps = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
        ),
        title: 'No Technical Hiring Needed',
        description: 'Leave the vetting to us. We handle all technical assessments and matching.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
            </svg>
        ),
        title: 'Tech Lead Oversight Included',
        description: 'Every engagement includes tech lead oversight for quality assurance.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
        ),
        title: 'Predictable Monthly Cost',
        description: 'Simple subscription pricing. No hidden fees or surprise costs.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
            </svg>
        ),
        title: 'Local Malaysian Developers',
        description: 'Work with developers who understand your market and timezone.',
    },
];

// How it works steps
const steps = [
    {
        number: '1',
        title: 'Tell Us Your Needs',
        description: 'Share your requirements: team size, junior or senior developers, and preferred tenure.',
    },
    {
        number: '2',
        title: 'We Match & Assign',
        description: 'CODEMAKER vets and matches the right developers to your project requirements.',
    },
    {
        number: '3',
        title: 'Start Building',
        description: 'Work directly with your developer daily while we provide Tech Lead oversight.',
    },
];

// Audience data
const audiences = [
    {
        icon: <BuildingIcon />,
        title: 'Large Enterprises',
        description: 'Scale your development teams without the overhead of permanent hires. Get access to vetted developers with enterprise-grade support.',
        features: [
            'Dedicated account management',
            'Flexible team scaling',
            'Enterprise SLA support',
            'Multi-project coordination',
        ],
    },
    {
        icon: <UsersIcon />,
        title: 'SMEs & Startups',
        description: 'Access senior-level talent at a fraction of the cost. Accelerate your digital transformation without breaking the budget.',
        features: [
            'Cost-effective solutions',
            'Quick onboarding process',
            'Startup-friendly terms',
            'Tech Lead guidance included',
        ],
    },
];

// Why CODEMAKER points
const whyPoints = [
    {
        title: 'Malaysia\'s First TaaS Platform',
        description: 'Pioneer in Talent-as-a-Service, specifically designed for the Malaysian market.',
    },
    {
        title: 'Expert Technical Leadership',
        description: 'Every project benefits from Tech Lead oversight and code quality reviews.',
    },
    {
        title: 'Flexible Engagement Terms',
        description: 'From 3 months to long-term partnerships, choose what works for your business.',
    },
    {
        title: 'Seamless Communication',
        description: 'Direct daily communication with developers, no middlemen or delays.',
    },
];

function Home() {
    return (
        <>
            <Helmet>
                <title>CODEMAKER Technology | Talent as a Service (TaaS) in Malaysia</title>
                <meta
                    name="description"
                    content="Hire managed developers on demand in Malaysia. CODEMAKER provides enterprise-grade Talent-as-a-Service with junior and senior developers, Tech Lead oversight, and predictable monthly pricing."
                />
            </Helmet>

            <main>
                {/* Hero Section */}
                <section className="hero">
                    <div className="hero__container">
                        <div className="hero__content">
                            <span className="hero__badge">Malaysia's First TaaS Platform</span>
                            <h1 className="hero__title">
                                Hire Managed Developers <span>on Demand</span> in Malaysia
                            </h1>
                            <p className="hero__description">
                               CODEMAKER Technology offers enterprise-level Talent-as-a-Service (TaaS) in 
                               Malaysia, allowing companies to hire or outsource system developers 
                               backed by Tech Lead supervision. Achieve faster digital transformation 
                               with high-quality developers, flexible team scaling, and fixed monthly 
                               costs.
                            </p>
                            <div className="hero__actions">
                                <Button as={Link} to="/contact" variant="primary" size="large">
                                    Book a Consultation
                                </Button>
                                <Button as={Link} to="/pricing" variant="ghost" size="large">
                                    View Pricing
                                </Button>
                            </div>
                        </div>
                        <div className="hero__visual">
                            <div className="hero__visual-graphic" aria-hidden="true">
                                <div className="hero__visual-circle hero__visual-circle--1"></div>
                                <div className="hero__visual-circle hero__visual-circle--2"></div>
                                <div className="hero__visual-circle hero__visual-circle--3"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Trusted By Section (social proof placeholder) */}
                <section className="trusted">
                    <div className="trusted__container">
                        <p className="trusted__label">Trusted by companies across Malaysia</p>
                        <div className="trusted__logos">
                            <img
                                src="/client-1.png"
                                alt="Client Logo"
                                className="client_logo-image"
                            />
                            <img
                                src="/client-2.png"
                                alt="Client Logo"
                                className="client_logo-image"
                            />
                            <img
                                src="/client-3.png"
                                alt="Client Logo"
                                className="client_logo-image"
                            />
                            <img
                                src="/client-4.png"
                                alt="Client Logo"
                                className="client_logo-image"
                            />
                            <img
                                src="/client-5.svg"
                                alt="Client Logo"
                                className="client_logo-image"
                            />
                            
                        </div>
                    </div>
                </section>

                {/* Value Propositions Section */}
                <Section id="value-props">
                    <Section.Header
                        subtitle="Why Choose TaaS"
                        title="Everything You Need to Build Faster"
                        description="Stop worrying about hiring, vetting, and managing developers. We handle it all so you can focus on your business."
                    />
                    <div className="value-props__grid">
                        {valueProps.map((prop, index) => (
                            <Card key={index} variant="elevated" centered padding="spacious">
                                <Card.Icon>{prop.icon}</Card.Icon>
                                <Card.Title>{prop.title}</Card.Title>
                                <Card.Description>{prop.description}</Card.Description>
                            </Card>
                        ))}
                    </div>
                </Section>

                {/* How It Works Section */}
                <Section id="how-it-works" background="light">
                    <Section.Header
                        subtitle="Simple Process"
                        title="How It Works"
                        description="Getting started with CODEMAKER is quick and straightforward. Here's your path to a dedicated development team."
                    />
                    <div className="how-it-works__steps">
                        {steps.map((step, index) => (
                            <div key={index} className="step">
                                <div className="step__number">{step.number}</div>
                                <h3 className="step__title">{step.title}</h3>
                                <p className="step__description">{step.description}</p>
                                {index < steps.length - 1 && <div className="step__arrow" aria-hidden="true"></div>}
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 'var(--space-10)' }}>
                        <Button as={Link} to="/how-it-works" variant="secondary" size="large">
                            Learn More About Our Process
                        </Button>
                    </div>
                </Section>

                {/* Who Is This For Section */}
                <Section id="audience">
                    <Section.Header
                        subtitle="Who We Serve"
                        title="Built for Malaysian Businesses"
                        description="Whether you're a large enterprise or a growing SME, CODEMAKER provides the right talent solution for your needs."
                    />
                    <div className="audience__grid">
                        {audiences.map((audience, index) => (
                            <Card key={index} variant="default" padding="spacious">
                                <div className="audience-card">
                                    <div className="audience-card__icon">{audience.icon}</div>
                                    <h3 className="audience-card__title">{audience.title}</h3>
                                    <p className="audience-card__description">{audience.description}</p>
                                    <div className="audience-card__features">
                                        {audience.features.map((feature, idx) => (
                                            <div key={idx} className="audience-card__feature">
                                                <CheckIcon />
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Section>

                {/* Why CODEMAKER Section */}
                <Section id="why-codemaker" background="light">
                    <div className="why-codemaker__content">
                        <div className="why-codemaker__text">
                            <h2>Why Choose CODEMAKER?</h2>
                            <p>
                                We're not just another outsourcing company. We're your strategic partner
                                in building exceptional software with exceptional people.
                            </p>
                            <div className="why-codemaker__list">
                                {whyPoints.map((point, index) => (
                                    <div key={index} className="why-codemaker__item">
                                        <div className="why-codemaker__item-icon">
                                            <CheckIcon />
                                        </div>
                                        <div className="why-codemaker__item-text">
                                            <strong>{point.title}</strong>
                                            <span>{point.description}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="why-codemaker__visual">
                            <div className="why-codemaker__stats">
                                <div className="stat">
                                    <div className="stat__value">5+</div>
                                    <div className="stat__label">Ongoing Projects</div>
                                </div>
                                <div className="stat">
                                    <div className="stat__value">10+</div>
                                    <div className="stat__label">Projects Delivered</div>
                                </div>
                                <div className="stat">
                                    <div className="stat__value">100%</div>
                                    <div className="stat__label">Client Satisfaction</div>
                                </div>
                                <div className="stat">
                                    <div className="stat__value">7+</div>
                                    <div className="stat__label">Years Experience</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* CTA Section */}
                <Section background="gradient" spacing="large">
                    <div className="cta">
                        <h2 className="cta__title">Ready to Transform Your Development?</h2>
                        <p className="cta__description">
                            Join Malaysian enterprises and SMEs who are already building faster with CODEMAKER's
                            Talent-as-a-Service. Book a free consultation today.
                        </p>
                        <div className="cta__actions">
                            <Button as={Link} to="/contact" variant="primary" size="large">
                                Book a Free Consultation
                            </Button>
                            <Button as={Link} to="/pricing" variant="ghost" size="large">
                                See Pricing Plans
                            </Button>
                        </div>
                    </div>
                </Section>
            </main>
        </>
    );
}

export default Home;
